import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Medal } from '@phosphor-icons/react';
import Navbar from '../components/Navbar';
import UserProfileModal from '../components/UserProfileModal';
import { formatCurrency, formatPercent } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import TeenVestLoader from '../components/TeenVestLoader';

const Leaderboard = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUsername, setSelectedUsername] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get('/leaderboard');
        setLeaderboard(response.data);
      } catch (error) {
        toast.error('Failed to load leaderboard');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return <Trophy size={28} weight="fill" className="text-yellow-400" />;
      case 2: return <Medal size={28} weight="fill" className="text-zinc-300" />;
      case 3: return <Medal size={28} weight="fill" className="text-amber-600" />;
      default: return <span className="text-xl font-mono font-bold text-zinc-600">#{rank}</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <TeenVestLoader size={112} label="Loading leaderboard..." />
        </div>
      </div>
    );
  }

  const userRow = leaderboard.find(item => item.username === user?.username);

  return (
    <div className="min-h-screen noise-bg" data-testid="leaderboard-page">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600 mb-6">
            <Trophy size={48} weight="fill" className="text-black" />
          </div>
          <h1 className="text-4xl md:text-5xl font-unbounded font-bold text-white mb-3">Leaderboard</h1>
          <p className="text-zinc-400">Ranked by <span className="text-gold font-bold">Trading Profit</span> only — game-mode bonus cash is excluded so stock skill wins.</p>
          <p className="text-xs text-zinc-500 mt-1">Click any player to view their portfolio & achievements</p>
        </motion.div>

        {/* Your Rank Card */}
        {userRow && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6 mb-8 border-2 border-gold cursor-pointer hover:scale-[1.01] transition-transform"
            data-testid="user-rank-card"
            onClick={() => setSelectedUsername(user.username)}
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-5">
                <div className="flex items-center justify-center w-14 h-14">
                  {getRankIcon(userRow.rank)}
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Your Rank</p>
                  <p className="text-xl font-unbounded font-bold text-gold">
                    {userRow.nickname || user?.username}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs font-mono text-zinc-400">{userRow.xp || 0} XP</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total Wealth</p>
                <p className="text-2xl font-mono font-bold text-white">{formatCurrency(userRow.total_value)}</p>
                <p className={`font-mono font-bold ${userRow.profit >= 0 ? 'text-success' : 'text-destructive'}`}>
                  Trading: {userRow.profit >= 0 ? '+' : ''}{formatCurrency(userRow.profit)}
                </p>
                {(userRow.bonus_earnings || 0) > 0 && (
                  <p className="text-xs font-mono text-zinc-500 mt-0.5">
                    + {formatCurrency(userRow.bonus_earnings)} game bonus
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Leaderboard Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="glass-card overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="leaderboard-table">
              <thead className="border-b border-white/10 bg-white/5">
                <tr>
                  <th className="text-left p-4 text-xs font-medium text-zinc-400 uppercase tracking-wide">#</th>
                  <th className="text-left p-4 text-xs font-medium text-zinc-400 uppercase tracking-wide">Player</th>
                  <th className="text-right p-4 text-xs font-medium text-zinc-400 uppercase tracking-wide hidden sm:table-cell">XP</th>
                  <th className="text-right p-4 text-xs font-medium text-zinc-400 uppercase tracking-wide">Trading P/L</th>
                  <th className="text-right p-4 text-xs font-medium text-zinc-400 uppercase tracking-wide">Return</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((item, index) => {
                  const isCurrentUser = item.username === user?.username;
                  return (
                    <motion.tr
                      key={item.username}
                      initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      onClick={() => setSelectedUsername(item.username)}
                      className={`border-b border-white/5 last:border-0 cursor-pointer transition-colors ${
                        isCurrentUser ? 'bg-gold/10 hover:bg-gold/15' : 'hover:bg-white/5'
                      }`}
                      data-testid={`leaderboard-row-${index}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center justify-center w-10">
                          {getRankIcon(item.rank)}
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className={`font-bold ${isCurrentUser ? 'text-gold' : 'text-white'}`}>
                            {item.nickname || item.username}
                            {isCurrentUser && <span className="ml-2 text-xs text-gold">(You)</span>}
                            {!item.has_traded && (
                              <span className="ml-2 text-[10px] px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded-full">
                                Inactive
                              </span>
                            )}
                          </p>
                          {item.nickname && (
                            <p className="text-xs text-zinc-500">@{item.username}</p>
                          )}
                          {(item.achievements?.length > 0) && (
                            <p className="text-xs text-zinc-500 mt-0.5 hidden sm:block">
                              {item.achievements.slice(0, 3).join(' · ')}
                              {item.achievements.length > 3 && ` +${item.achievements.length - 3}`}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-right font-mono text-gold font-bold hidden sm:table-cell">
                        {item.xp || 0}
                      </td>
                      <td className="p-4 text-right">
                        <div className={`font-mono font-bold ${item.profit >= 0 ? 'text-success' : 'text-destructive'}`}>
                          {item.profit >= 0 ? '+' : ''}{formatCurrency(item.profit)}
                        </div>
                        {(item.bonus_earnings || 0) > 0 && (
                          <div className="text-[10px] font-mono text-zinc-500" title="Game-mode bonus cash (excluded from rank)">
                            + {formatCurrency(item.bonus_earnings)} bonus
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <span className={`font-mono font-bold ${item.profit_percent >= 0 ? 'text-success' : 'text-destructive'}`}>
                          {formatPercent(item.profit_percent)}
                        </span>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="mt-6 p-4 bg-gradient-to-r from-red-900/20 to-orange-900/20 border border-red-500/30 rounded-xl"
        >
          <p className="text-orange-400 text-sm text-center">
            💸 <strong>Stay Active!</strong> Users with 0 stocks lose $1,000/week. Buy just 1 stock to escape the debt forever.
          </p>
        </motion.div>
      </div>

      {selectedUsername && (
        <UserProfileModal
          username={selectedUsername}
          onClose={() => setSelectedUsername(null)}
        />
      )}
    </div>
  );
};

export default Leaderboard;
